import axios from 'axios';

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/api',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor for adding auth token and device ID
api.interceptors.request.use(
    (config) => {
        // Only add token for protected routes
        const isPublicRoute = window.location.pathname.startsWith('/table/') || 
                            window.location.pathname.startsWith('/payment/');
        
        if (!isPublicRoute) {
            const token = localStorage.getItem('token');
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        const deviceId = localStorage.getItem('deviceId');
        if (deviceId) {
            config.headers['x-device-id'] = deviceId;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for handling errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Handle user not found during QR code scan
            if (error.response?.data?.code === 'USER_NOT_FOUND') {
                // Clear user data and force re-registration
                const deviceId = localStorage.getItem('deviceId');
                if (deviceId) {
                    localStorage.removeItem(`user_${deviceId}`);
                }
                
                // Store QR code information for after user registration
                // Extract QR code from URL if this was a QR scan
                const currentUrl = window.location.href;
                const qrMatch = currentUrl.match(/\/table\/qr\/([^?]+)/);
                const hasFromQRCode = currentUrl.includes('fromQRCode=true');
                
                if (qrMatch && hasFromQRCode) {
                    const qrCode = qrMatch[1];
                    console.log('[API] Storing pending QR code session:', qrCode);
                    localStorage.setItem('pendingQRCode', qrCode);
                    localStorage.setItem('pendingFromQRCode', 'true');
                }
                
                // Clear Redux user state to trigger UserNamePrompt
                // We'll dispatch this through a custom event since we can't import Redux here
                window.dispatchEvent(new CustomEvent('clearUserState'));
                
                console.error('User not found during QR scan - must complete registration first');
                return Promise.reject(error);
            }
            
            // Only redirect to login for protected routes
            const isPublicRoute = window.location.pathname.startsWith('/table/') || 
                                window.location.pathname.startsWith('/payment/');
            if (!isPublicRoute) {
                localStorage.removeItem('token');
                localStorage.removeItem('deviceId');
                window.location.href = '/login';
            }
        } else if (error.response?.status === 403 && error.response?.data?.code === 'SESSION_INVALID') {
            // Handle session invalid errors
            console.error('SESSION_INVALID error:', {
                url: error.config?.url,
                method: error.config?.method,
                headers: error.config?.headers,
                data: error.response?.data,
                timestamp: new Date().toISOString()
            });
            
            // Remove stored table ID to force re-scanning
            localStorage.removeItem('tableId');
            
            // Navigate to session expired page
            window.location.href = '/session-expired';
        } else if (error.response?.status >= 500) {
            // Handle server errors (5xx) - redirect to general error page
            console.error('Server error:', {
                status: error.response?.status,
                url: error.config?.url,
                method: error.config?.method,
                data: error.response?.data,
                timestamp: new Date().toISOString()
            });
            
            // Only redirect for public routes to avoid interfering with authenticated areas
            const isPublicRoute = window.location.pathname.startsWith('/table/') || 
                                window.location.pathname.startsWith('/payment/');
            if (isPublicRoute) {
                window.location.href = '/error';
            }
        }
        return Promise.reject(error);
    }
);

export const callWaiter = (tableId: string) => api.post(`/tables/${tableId}/call-waiter`);

export default api; 